import { ReactNode, useMemo } from "react";
import { GrClose } from "react-icons/gr";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "../../../components/Box";
import {
  Breadcrumb,
  StaticBreadcrumb,
  Crumb,
  BreadcrumbDivider,
} from "../../../components/Breadcrumbs";
import { Flex } from "../../../components/Flex";
import { IconButton } from "../../../components/IconButton";
import Topbar from "../../../components/Topbar";
import { ProgressBar } from "../../../components/ProgressBar";

type ScreeningMode = "entity" | "person";

export type ScreeningStep =
  | "selectChecks"
  | "addEntity"
  | "addPerson"
  | "addAddress"
  | "review";

const titles: Record<ScreeningStep, string> = {
  selectChecks: "Select Checks",
  addEntity: "Entity Details",
  addPerson: "Individual Details",
  addAddress: "Add Address",
  review: "Review",
};

export const NewProfileScreeningFrame = ({
  children,
  step,
  mode,
}: {
  children: ReactNode;
  step: ScreeningStep;
  mode: ScreeningMode;
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const steps: ScreeningStep[] = useMemo(() => {
    // ✅ Entity flow has NO address step (3 steps)
    if (mode === "entity") return ["selectChecks", "addEntity", "review"];

    // ✅ Individual flow includes address step (4 steps)
    return ["selectChecks", "addPerson", "addAddress", "review"];
  }, [mode]);

  const currentStepIndex = Math.max(0, steps.indexOf(step));
  const progress = ((currentStepIndex + 1) * 100) / steps.length;

  const crumbs = steps.map((s, idx) => ({
    key: idx + 1,
    isActive: s === step,
    title: `${String(idx + 1).padStart(2, "0")} ${titles[s]}`,
  }));

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <Breadcrumb>
              <Breadcrumb.Section linkTo={`/profile/${profileId}`}>
                Profiles
              </Breadcrumb.Section>
              <Breadcrumb.Section>Create a new check</Breadcrumb.Section>
            </Breadcrumb>

            <Flex>
              <IconButton
                Type={GrClose}
                onClick={() => navigate(`/profile/${profileId}`)}
                color="gray.40"
                ml={4}
                mr={3}
              />
            </Flex>
          </Flex>
        }
      />

      <Box height="calc(100vh - 155px)" overflowY="auto">
        <Box>
          <ProgressBar progress={progress} />
          <Box padding={2} marginLeft={2}>
            <StaticBreadcrumb>
              {crumbs.map(({ isActive, title, key }, index) => (
                <Crumb
                  key={key}
                  isActive={isActive}
                  iconDivider={
                    index < crumbs.length - 1 && <BreadcrumbDivider />
                  }
                >
                  {title}
                </Crumb>
              ))}
            </StaticBreadcrumb>
          </Box>
        </Box>

        <Flex position="relative" justifyContent="center" alignItems="center">
          <Box>{children}</Box>
        </Flex>
      </Box>
    </div>
  );
};
